import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Wifi, WifiOff, Smartphone } from "lucide-react";

/**
 * PWA Install Prompt — shows a dismissable banner prompting user to install the app.
 * Also shows an offline indicator when connection is lost.
 */
export default function PWAInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dismissed, setDismissed] = useState(false);

  // Listen for beforeinstallprompt event
  useEffect(() => {
    const wasDismissed = localStorage.getItem("les_lex_pwa_dismissed");
    if (wasDismissed) {
      setDismissed(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!wasDismissed) {
        // Show banner after 30 seconds of usage
        setTimeout(() => setShowBanner(true), 30000);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setShowBanner(false);
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => setIsOffline(false);
    const goOffline = () => setIsOffline(true);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") {
      setShowBanner(false);
    }
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem("les_lex_pwa_dismissed", "true");
  };

  return (
    <>
      {/* Offline indicator */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-yellow-500 text-yellow-950 text-center py-1.5 px-4 text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="h-4 w-4" />
          Pas de connexion Internet — certaines fonctions peuvent être indisponibles
        </div>
      )}

      {/* Install prompt banner */}
      {showBanner && !dismissed && installPrompt && (
        <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[60] animate-in slide-in-from-bottom-4">
          <div className="bg-white dark:bg-gray-900 border border-primary/20 rounded-xl shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Smartphone className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm">Installer LES-LEX</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Installez l'application sur votre appareil — elle fonctionnera comme une app native.
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleInstall} className="h-8 text-xs">
                    <Download className="h-3.5 w-3.5 mr-1.5" /> Installer
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleDismiss} className="h-8 text-xs">
                    Pas maintenant
                  </Button>
                </div>
              </div>
              <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
